import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TextEyePassword } from "./TextEyePassword";
import { Box } from "@mui/system";
import {
  IconButton,
  VaporIcon,
  Button,
  Spinner,
  NotificationOverflow,
  NotificationInline,
  AlertTitle,
  Toast,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Card,
  CardActions,
} from "@vapor/v3-components";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { getLogonInfoFromCookies } from "../utils/util";
// import { useWindowWidth } from "../utils/useWindowWidth";
import {
  getListActions,
  executeAction,
} from "../services/filterDetailsService";
import {
  RowFilterActionsResponse,
  DmsAction,
} from "../services/Models/FilterDetailsModel";

interface Action {
  idAction: number;
  actionName: string;
}

interface ListActionsProps {
  idFilter: number;
  idObject: number;
  open: boolean;
  onClose: (actionExecuted: boolean, actionName?: string) => void;
}

export const ListActions: React.FC<ListActionsProps> = ({
  idFilter,
  idObject,
  open,
  onClose,
}) => {
  // const loginResult = useRef<AuthResponse>(getLogonInfoFromCookies());
  const { t } = useTranslation();

  // const windowWidth = useWindowWidth(); // Larghezza della finestra
  // const actionWidth = Math.max(windowWidth * 0.3, 500); // Larghezza minima 50px
  const actionWidth = 400; // Larghezza minima 50px

  const [error, setError] = useState<string | null>(null);
  const [saveOK, setSaveOK] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionList, setActionList] = useState<Action[]>([]);

  const callGetListActions = async () => {
    if (idObject >= 0) {
      const loginResult = getLogonInfoFromCookies();
      if (loginResult && loginResult.SecurityToken && !loading) {
        setLoading(true);
        try {
          const result: RowFilterActionsResponse = await getListActions(
            loginResult.SecurityToken,
            loginResult.Language,
            idFilter,
            idObject
          );
          if (result && result.ResultCode !== "ERROR") {
            console.log("getListActions successful:");
            if (result.Actions.length === 0) {
              setError(t("no-actions"));
              onClose(false);
            } else {
              const arrayActions = result.Actions.map((action: DmsAction) => {
                const appAction: Action = {
                  idAction: action.IdAction,
                  actionName: action.ActionName,
                };
                return appAction;
              });
              setActionList(arrayActions);
            }
          } else {
            setError(t("errore interno") + " " + result.DetailedMessage);
            console.error("getListActions Error :", result.DetailedMessage);
            onClose(false);
          }
        } catch (error) {
          setError(t("errore interno") + " " + error);
          console.error("getListActions Error :", error);
          onClose(false);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    if (idObject > 0) callGetListActions();
  }, [idObject]);

  const onClickAction = async (idAction: number) => {
    if (idAction >= 0) {
      const loginResult = getLogonInfoFromCookies();
      if (loginResult && loginResult.SecurityToken && !loading) {
        setLoading(true);
        try {
          const result: RowFilterActionsResponse = await executeAction(
            loginResult.SecurityToken,
            loginResult.Language,
            idFilter,
            idObject,
            idAction
          );
          if (result && result.ResultCode !== "ERROR") {
            console.log("executeAction successful:");
            onClose(
              true,
              actionList!.find((item: Action) => item.idAction === idAction)!
                .actionName
            );
          } else {
            setError(t("errore interno") + " " + result.DetailedMessage);
            console.error("executeAction Error :", result.DetailedMessage);
          }
        } catch (error) {
          setError(t("errore interno") + " " + error);
          console.error("executeAction Error :", error);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  return (
    <Dialog
      aria-describedby="alert-dialog-description"
      aria-labelledby="alert-dialog-title"
      // fullWidth={true}
      // maxWidth={"lg"}
      open={open}
    >
      <DialogTitle>
        {t("action-list")}
        <IconButton
          color="primary"
          onClick={() => {
            onClose(false);
          }}
          variant="text"
        >
          <VaporIcon icon={faXmark} size="s" color="primary" />
        </IconButton>
      </DialogTitle>
      <Divider variant="fullWidth" />
      <DialogContent sx={{ p: 2 }}>
        <Box
          width="100%"
          height="100%"
          display="flex" // Aggiunto per abilitare il layout flex
          flexDirection="column" // Per disporre gli elementi verticalmente
          alignItems="center" // Centra orizzontalmente gli elementi
          justifyContent="center" // Centra verticalmente gli elementi
          margin="10"
        >
          <Grid
            container
            spacing={2}
            justifyContent="flex-start"
            marginBottom={1}
            marginLeft={2}
            marginRight={2}
            width={actionWidth + 30}
          >
            {actionList.map((action: Action, index: number) => (
              <Grid item key={index}>
                {" "}
                {/* Ogni elemento figlio ha margini definiti da spacing */}
                <Card>
                  <CardActions>
                    <Button
                      variant="contained"
                      onClick={() => onClickAction(action.idAction)}
                      sx={{
                        width: "400px !important",
                      }}
                    >
                      {action.actionName}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          sx={{
            mr: 1,
          }}
          onClick={() => {
            // setActiveSave(true);
            onClose(false);
          }}
          color="primary"
        >
          {t("cancel")}{" "}
        </Button>
      </DialogActions>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)", // Sfondo semitrasparente opzionale
          }}
        >
          <Spinner size={64} />
        </Box>
      )}
      {error && (
        <NotificationOverflow severity="error" onClose={() => setError(null)}>
          <AlertTitle>{t("error-header")}</AlertTitle>
          {error}
        </NotificationOverflow>
      )}
      <Toast
        open={saveOK ? true : false}
        anchorOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        autoHideDuration={2000}
        onClose={() => setSaveOK(null)}
      >
        <NotificationInline severity="success" onClose={() => setSaveOK(null)}>
          <AlertTitle>{saveOK}</AlertTitle>
          {t("actionOK")}
        </NotificationInline>
      </Toast>
    </Dialog>
  );
};
